import { useWindowSize } from "@iventis/utilities/src/hooks/use-window-size";
import { CustomDialogAnchor, FormWizardTemplate, FormWizardTitle, StyledCustomDialog } from "@iventis/components";
import React, { useRef, useState } from "react";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { TitleWithIcon, styled } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { RepeatedTimeRangeValueComponent, RepeatedTimeRangeValueRef } from "@iventis/repeated-time-ranges";
import { parseRepeatedTimeRangeDataFieldValueToString } from "@iventis/utilities";
import { AttributeListItemEditor, AttributeNameEditor, StyledGridRadioGroup } from "./attribute-editor-components";
import { PredefinedAttributeOption } from "./add-attribute-form";
import { AttributeDefaultValueComponent } from "./attribute-default-value";
import { DataFieldServicesContext, useDataFieldServices } from "./data-fields-services";

/** Will list each given attribute with a radio button */
export const AddPredefinedAttributeForm = ({
    attributes,
    saveAttribute,
    saveDefaultValue,
    onClose,
}: {
    attributes: DataField[];
    saveAttribute: (dataField: DataField) => Promise<void>;
    saveDefaultValue: (dataField: DataField) => Promise<void>;
    onClose: () => void;
}) => {
    const translate = useIventisTranslate();
    const { screenWidth } = useWindowSize();
    const [selectedAttribute, setSelectedAttribute] = useState<DataField | null>(null);

    const repeatedTimeRangeRef = useRef<RepeatedTimeRangeValueRef | null>(null);

    const [validation, setValidation] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stage, setState] = useState(0);
    const dataFieldServices = useDataFieldServices();

    const setDefaultValue = (defaultValue: DataField["defaultValue"]) => {
        setSelectedAttribute((att) => ({ ...att, defaultValue }));
    };

    const handListItemDefaultValueChange = (dataField: DataField) => {
        setSelectedAttribute((att) => ({ ...att, defaultValue: dataField.defaultValue }));
        return Promise.resolve();
    };

    const handleSaveAttributeClicked = async () => {
        setSaving(true);
        if (selectedAttribute.type === DataFieldType.RepeatedTimeRanges) {
            const repeatedTimeRangeDefaultValue = repeatedTimeRangeRef.current?.getValues();
            if (!repeatedTimeRangeDefaultValue) {
                await saveAttribute(selectedAttribute);
            } else {
                const defaultValue: DataField["defaultValue"] = {
                    dataFieldId: selectedAttribute.id,
                    valueText: null,
                    valueRepeatedTimeRanges: parseRepeatedTimeRangeDataFieldValueToString(repeatedTimeRangeDefaultValue),
                };
                await saveAttribute({ ...selectedAttribute, defaultValue });
            }
        } else {
            await saveAttribute(selectedAttribute);
        }
        setSaving(false);
        setState(1);
    };

    const handleSaveDefaultValueClicked = async () => {
        setSaving(true);
        await saveDefaultValue(selectedAttribute);
        setSaving(false);
        onClose();
    };

    return (
        <StyledCustomDialog $fullHeight open anchor={CustomDialogAnchor.TOP} $minWidth="800px" onClose={onClose} $screenWidth={screenWidth} $alternativePadding>
            <FormWizardTemplate
                stages={[
                    {
                        isValid: selectedAttribute != null,
                        primaryButtonCallback: handleSaveAttributeClicked,
                        primaryButtonText: translate(Content.map.data_fields.add),
                        submitButtonDataCy: "add-attributes-button",
                        replaceTemplateWithComponent: false,
                        secondaryButtons: [{ buttonText: translate(Content.common.buttons.close), onButtonPressed: onClose, buttonDisabled: saving }],
                        Component: (
                            <StyledGridRadioGroup value={selectedAttribute?.id} onChange={(_, value) => setSelectedAttribute(attributes.find((df) => df.id === value))}>
                                {attributes.map((attribute) => (
                                    <PredefinedAttributeOption attribute={attribute} key={attribute.id} />
                                ))}
                            </StyledGridRadioGroup>
                        ),
                    },
                    {
                        isValid: selectedAttribute?.defaultValue != null && (selectedAttribute.type !== DataFieldType.RepeatedTimeRanges || validation),
                        primaryButtonCallback: handleSaveDefaultValueClicked,
                        primaryButtonText: selectedAttribute?.defaultValue != null ? translate(Content.common.buttons.save) : translate(Content.common.saved),
                        submitButtonDataCy: "add-default-value-attributes-button",
                        showLoadingSpinner: saving,
                        replaceTemplateWithComponent: false,
                        secondaryButtons: [
                            { buttonText: translate(Content.common.buttons.close), onButtonPressed: onClose, buttonDisabled: saving, dataCy: "close-attribute-editor" },
                        ],
                        Component: (
                            <DataFieldServicesContext.Provider
                                value={{ ...dataFieldServices, dataFieldsService: { ...dataFieldServices.dataFieldsService, putDataField: handListItemDefaultValueChange } }}
                            >
                                <DefaultValueContainer>
                                    <AttributeNameEditor disabled attribute={selectedAttribute} />
                                    {selectedAttribute != null &&
                                        selectedAttribute.type !== DataFieldType.List &&
                                        selectedAttribute.type !== DataFieldType.Image &&
                                        selectedAttribute.type !== DataFieldType.RepeatedTimeRanges && (
                                            <div>
                                                <AttributeDefaultValueComponent attribute={selectedAttribute} setDefaultValue={setDefaultValue} />
                                            </div>
                                        )}
                                    {selectedAttribute != null && selectedAttribute.type === DataFieldType.List && (
                                        <AttributeListItemEditor attribute={selectedAttribute} areListItemsSaving={() => false} disabled />
                                    )}
                                    {selectedAttribute != null && selectedAttribute.type === DataFieldType.RepeatedTimeRanges && (
                                        <RepeatedTimeRangeValueComponent
                                            initialValue={[]}
                                            attributeName={selectedAttribute.name}
                                            setValidation={setValidation}
                                            ref={repeatedTimeRangeRef}
                                            setValue={(value) =>
                                                setDefaultValue({
                                                    dataFieldId: selectedAttribute.id,
                                                    valueText: null,
                                                    valueRepeatedTimeRanges: parseRepeatedTimeRangeDataFieldValueToString(value),
                                                })
                                            }
                                        />
                                    )}
                                </DefaultValueContainer>
                            </DataFieldServicesContext.Provider>
                        ),
                    },
                ]}
                currentStage={stage}
                title={
                    <FormWizardTitle
                        footer={translate(Content.map4.attributes.add_attribute_subtitle)}
                        title={<TitleWithIcon icon={["fas", "tag"]} title={translate(Content.map.data_fields.add)} />}
                    />
                }
            />
        </StyledCustomDialog>
    );
};

const DefaultValueContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px 0px;
    flex-grow: 1;
`;
